import React, { useMemo } from 'react';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { Event } from '@models/event.model';
import { Task } from '@models/task.model';
import { SnackType } from '@models/common.model';
import { Modal } from '@components/molecules/Modal';
import { Input } from '@components/atoms/Input';
import { FormGroup } from '@components/molecules/form/FormGroup';
import ReactSelect from '@components/atoms/ReactSelect';
import { useCreateEvent, useUpdateEvent } from '@hooks/api';
import { useDispatch } from 'react-redux';
import { appendActionMessage } from '@redux/reducers/actionMessages.reducer';
import {
  TimeOptions,
  EventTypeOptions,
  IncidentTypeOptions,
  InspectionTypeOptions,
} from '@utils/options';
import { useTranslation } from '@hooks/useTranslation';

interface CreateEditEventModalProps {
  isOpen: boolean;
  onClose: () => void;
  event?: Event | null;
  projectId: string;
  tasks: Task[];
  onSuccess?: () => void;
}

interface EventFormValues {
  name: string;
  eventType: string;
  otherEventType: string | null;
  eventTime: string;
  incidentType: string | null;
  addedIncidentType: string | null;
  inspectionType: string | null;
  permit: string | null;
  delay: number | null;
  delayType: string | null;
  notes: string | null;
  precedentTaskId: string[];
}

const buildValidationSchema = (t: Record<string, string>) =>
  Yup.object().shape({
    name: Yup.string().min(3, t.nameMinLength).required(t.nameRequired),
    eventType: Yup.string().required(t.eventTypeRequired),
    eventTime: Yup.string().required(t.eventTimeRequired),
    otherEventType: Yup.string()
      .nullable()
      .when('eventType', {
        is: 'Other',
        then: (schema) =>
          schema.min(3, t.otherEventTypeMinLength).required(t.otherEventTypeRequired),
        otherwise: (schema) => schema.nullable(),
      }),
    incidentType: Yup.string()
      .nullable()
      .when('eventType', {
        is: 'Incident',
        then: (schema) => schema.required(t.incidentTypeRequired),
        otherwise: (schema) => schema.nullable(),
      }),
    addedIncidentType: Yup.string()
      .nullable()
      .when(['eventType', 'incidentType'], {
        is: (eventType: string, incidentType: string) =>
          eventType === 'Incident' && incidentType === 'Add incident type',
        then: (schema) =>
          schema
            .min(3, t.addedIncidentTypeMinLength)
            .required(t.addedIncidentTypeRequired),
        otherwise: (schema) => schema.nullable(),
      }),
    inspectionType: Yup.string()
      .nullable()
      .when('eventType', {
        is: 'Inspection',
        then: (schema) => schema.required(t.inspectionTypeRequired),
        otherwise: (schema) => schema.nullable(),
      }),
    permit: Yup.string()
      .nullable()
      .when('eventType', {
        is: 'Permit',
        then: (schema) =>
          schema.min(3, t.permitMinLength).required(t.permitRequired),
        otherwise: (schema) => schema.nullable(),
      }),
    delay: Yup.number()
      .nullable()
      .when('eventType', {
        is: 'Incident',
        then: (schema) =>
          schema.min(0, t.delayPositive).required(t.delayRequired),
        otherwise: (schema) => schema.nullable(),
      }),
    delayType: Yup.string()
      .nullable()
      .when('eventType', {
        is: 'Incident',
        then: (schema) => schema.required(t.delayTypeRequired),
        otherwise: (schema) => schema.nullable(),
      }),
    notes: Yup.string()
      .nullable()
      .when('eventType', {
        is: (eventType: string) =>
          ['Incident', 'Visitor', 'Permit', 'Communication'].includes(eventType),
        then: (schema) =>
          schema.min(3, t.notesMinLength).required(t.notesRequired),
        otherwise: (schema) => schema.nullable(),
      }),
    precedentTaskId: Yup.array()
      .of(Yup.string())
      .when('eventType', {
        is: 'Inspection',
        then: (schema) => schema.min(1, t.atLeastOnePrecedingTask),
        otherwise: (schema) => schema.nullable(),
      }),
  });

const getInitialValues = (event?: Event | null): EventFormValues => {
  const values = {
    name: event?.name || '',
    eventType: event?.eventType || '',
    otherEventType: event?.otherEventType || null,
    eventTime: event?.eventTime
      ? new Date(event.eventTime).toISOString().split('T')[0]
      : '',
    incidentType: event?.incidentType || null,
    addedIncidentType: event?.addedIncidentType || null,
    inspectionType: event?.inspectionType || null,
    permit: event?.permit || null,
    delay: event?.delay || null,
    delayType: event?.delayType || null,
    notes: event?.notes || null,
    precedentTaskId: event?.precedentTaskId || [],
  };

  return values;
};

// Transform options to match SelectOption format
const transformOptions = (options: { id: string; name: string }[]) => {
  return options.map((option) => ({
    value: option.id,
    label: option.name,
  }));
};

export const CreateEditEventModal: React.FC<CreateEditEventModalProps> = ({
  isOpen,
  onClose,
  event,
  projectId,
  tasks,
  onSuccess,
}) => {
  const t = useTranslation('eventModal');
  const validationSchema = useMemo(() => buildValidationSchema(t), [t]);
  // Sprint 28, develop run 3 (story-144 review Critical #1 fix). `TimeOptions` (@utils/options)
  // is a non-component module and stays English-only by design (same rule as
  // `TaskStatusOptions`) — translate its labels here via the id-keyed map below.
  const translatedTimeOptionLabels: { [key: string]: string } = {
    hours: t.delayUnitHours,
    days: t.delayUnitDays,
    weeks: t.delayUnitWeeks,
  };
  const translatedTimeOptions = TimeOptions.map((option) => ({
    value: option.id,
    label: translatedTimeOptionLabels[option.id] || option.name,
  }));
  const dispatch = useDispatch();
  const { createEvent } = useCreateEvent();
  const { updateEvent } = useUpdateEvent();

  const handleSubmit = async (values: EventFormValues) => {
    try {
      const eventData = {
        ...values,
        projectId,
        eventTime: new Date(values.eventTime).toISOString(),
      };

      let result;
      if (event) {
        result = await updateEvent({
          ...eventData,
          projectId: event.projectId!,
          eventId: event.eventId!,
        });
      } else {
        result = await createEvent(eventData);
      }

      if (result) {
        dispatch(
          appendActionMessage({
            type: SnackType.SUCCESS,
            message: event ? t.eventUpdatedSuccess : t.eventCreatedSuccess,
          }),
        );
        onSuccess?.();
        onClose();
      }
    } catch (error) {
      dispatch(
        appendActionMessage({
          type: SnackType.ERROR,
          message: event ? t.eventUpdateFailed : t.eventCreateFailed,
        }),
      );
    }
  };

  if (!isOpen) return null;

  return (
    <Formik
      initialValues={getInitialValues(event)}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize>
      {({ values, setFieldValue, errors, touched, submitForm }: any) => (
        <Modal
          isOpen={isOpen}
          onClose={onClose}
          handleConfirm={() => submitForm()}
          confirmText={event ? t.saveChanges : t.createEvent}
          title={event ? t.editEvent : t.createEvent}>
          
          <Form className="space-y-4">
            {/* Name */}
            <FormGroup
              label={t.nameLabel}
              htmlFor="name"
              error={errors.name && touched.name ? errors.name : undefined}>
              <Field
                name="name"
                as={Input}
                placeholder={t.enterEventNamePlaceholder}
                className={errors.name && touched.name ? 'border-red-500' : ''}
              />
            </FormGroup>

            {/* Event Type */}
            <FormGroup
              label={t.eventTypeLabel}
              htmlFor="eventType"
              error={
                errors.eventType && touched.eventType
                  ? errors.eventType
                  : undefined
              }>
              <ReactSelect
                name="eventType"
                placeholder={t.selectEventTypePlaceholder}
                options={transformOptions(EventTypeOptions)}
                value={
                  values.eventType
                    ? { value: values.eventType, label: values.eventType }
                    : null
                }
                onChange={(value: any) => {
                  const newEventType = value?.value || '';
                  setFieldValue('eventType', newEventType);

                  // Reset dependent fields when event type changes
                  setFieldValue('otherEventType', null);
                  setFieldValue('incidentType', null);
                  setFieldValue('addedIncidentType', null);
                  setFieldValue('inspectionType', null);
                  setFieldValue('permit', null);
                  setFieldValue('delay', null);
                  setFieldValue('delayType', null);
                  setFieldValue('notes', null);
                  setFieldValue('precedentTaskId', []);

                  // Trigger validation to clear errors for hidden fields
                  setTimeout(() => {
                    setFieldValue('eventType', newEventType);
                  }, 0);
                }}
              />
            </FormGroup>

            {/* Other Event Type */}
            {values.eventType === 'Other' && (
              <FormGroup
                label={t.otherEventTypeLabel}
                htmlFor="otherEventType"
                error={
                  errors.otherEventType && touched.otherEventType
                    ? errors.otherEventType
                    : undefined
                }>
                <Field
                  name="otherEventType"
                  as={Input}
                  placeholder={t.enterOtherEventTypePlaceholder}
                  className={
                    errors.otherEventType && touched.otherEventType
                      ? 'border-red-500'
                      : ''
                  }
                />
              </FormGroup>
            )}

            {/* Event Time */}
            <FormGroup
              label={t.eventTimeLabel}
              htmlFor="eventTime"
              error={
                errors.eventTime && touched.eventTime
                  ? errors.eventTime
                  : undefined
              }>
              <Field
                name="eventTime"
                as={Input}
                type="date"
                placeholder={t.selectDatePlaceholder}
                className={
                  errors.eventTime && touched.eventTime ? 'border-red-500' : ''
                }
              />
            </FormGroup>

            {/* Incident Type */}
            {values.eventType === 'Incident' && (
              <FormGroup
                label={t.incidentTypeLabel}
                htmlFor="incidentType"
                error={
                  errors.incidentType && touched.incidentType
                    ? errors.incidentType
                    : undefined
                }>
                <ReactSelect
                  name="incidentType"
                  placeholder={t.selectIncidentTypePlaceholder}
                  options={transformOptions(IncidentTypeOptions)}
                  value={
                    values.incidentType
                      ? {
                          value: values.incidentType,
                          label: values.incidentType,
                        }
                      : null
                  }
                  onChange={(value: any) =>
                    setFieldValue('incidentType', value?.value || null)
                  }
                />
              </FormGroup>
            )}

            {/* Added Incident Type */}
            {values.eventType === 'Incident' &&
              values.incidentType === 'Add incident type' && (
                <FormGroup
                  label={t.addIncidentTypeLabel}
                  htmlFor="addedIncidentType"
                  error={
                    errors.addedIncidentType && touched.addedIncidentType
                      ? errors.addedIncidentType
                      : undefined
                  }>
                  <Field
                    name="addedIncidentType"
                    as={Input}
                    placeholder={t.enterIncidentTypePlaceholder}
                    className={
                      errors.addedIncidentType && touched.addedIncidentType
                        ? 'border-red-500'
                        : ''
                    }
                  />
                </FormGroup>
              )}

            {/* Inspection Type */}
            {values.eventType === 'Inspection' && (
              <FormGroup
                label={t.inspectionTypeLabel}
                htmlFor="inspectionType"
                error={
                  errors.inspectionType && touched.inspectionType
                    ? errors.inspectionType
                    : undefined
                }>
                <ReactSelect
                  name="inspectionType"
                  placeholder={t.selectInspectionTypePlaceholder}
                  options={transformOptions(InspectionTypeOptions)}
                  value={
                    values.inspectionType
                      ? {
                          value: values.inspectionType,
                          label: values.inspectionType,
                        }
                      : null
                  }
                  onChange={(value: any) =>
                    setFieldValue('inspectionType', value?.value || null)
                  }
                />
              </FormGroup>
            )}

            {/* Permit */}
            {values.eventType === 'Permit' && (
              <FormGroup
                label={t.permitLabel}
                htmlFor="permit"
                error={
                  errors.permit && touched.permit ? errors.permit : undefined
                }>
                <Field
                  name="permit"
                  as={Input}
                  placeholder={t.enterPermitNumberPlaceholder}
                  className={
                    errors.permit && touched.permit ? 'border-red-500' : ''
                  }
                />
              </FormGroup>
            )}

            {/* Delay and Delay Type */}
            {values.eventType === 'Incident' && (
              <div className="grid grid-cols-2 gap-4">
                <FormGroup
                  label={t.delayLabel}
                  htmlFor="delay"
                  error={
                    errors.delay && touched.delay ? errors.delay : undefined
                  }>
                  <Field
                    name="delay"
                    as={Input}
                    type="number"
                    placeholder="10"
                    className={
                      errors.delay && touched.delay ? 'border-red-500' : ''
                    }
                  />
                </FormGroup>
                <FormGroup
                  label={t.delayTypeLabel}
                  htmlFor="delayType"
                  error={
                    errors.delayType && touched.delayType
                      ? errors.delayType
                      : undefined
                  }>
                  <ReactSelect
                    name="delayType"
                    placeholder={t.selectTimeTypePlaceholder}
                    options={translatedTimeOptions}
                    value={
                      values.delayType
                        ? { value: values.delayType, label: values.delayType }
                        : null
                    }
                    onChange={(value: any) =>
                      setFieldValue('delayType', value?.value || null)
                    }
                  />
                </FormGroup>
              </div>
            )}

            {/* Notes */}
            {['Incident', 'Visitor', 'Permit', 'Communication'].includes(
              values.eventType,
            ) && (
              <FormGroup
                label={t.notesLabel}
                htmlFor="notes"
                error={
                  errors.notes && touched.notes ? errors.notes : undefined
                }>
                <Field
                  name="notes"
                  as={Input}
                  placeholder={t.enterNotesPlaceholder}
                  multiline
                  rows={4}
                  className={
                    errors.notes && touched.notes ? 'border-red-500' : ''
                  }
                />
              </FormGroup>
            )}

            {/* Preceding Tasks (for Inspection) */}
            {values.eventType === 'Inspection' && (
              <FormGroup
                label={t.precedingTasksLabel}
                htmlFor="precedentTaskId"
                error={
                  errors.precedentTaskId && touched.precedentTaskId
                    ? errors.precedentTaskId
                    : undefined
                }>
                <ReactSelect
                  name="precedentTaskId"
                  placeholder={t.selectPrecedingTasksPlaceholder}
                  options={tasks.map((task) => ({
                    value: task.taskId!,
                    label: task.name,
                  }))}
                  value={values.precedentTaskId.map((id: string) => {
                    const task = tasks.find((t) => t.taskId === id);
                    return { value: id, label: task?.name || id };
                  })}
                  onChange={(value: any) =>
                    setFieldValue(
                      'precedentTaskId',
                      Array.isArray(value)
                        ? value.map((v: any) => v.value)
                        : [],
                    )
                  }
                  isMulti
                />
              </FormGroup>
            )}
          </Form>
        </Modal>
      )}
    </Formik>
  );
};
